import { Component, OnDestroy, OnInit, Input } from '@angular/core';
import { Subject } from 'rxjs';
import { WorkflowRuleActionEdit } from '../../workflow-rule-action-edit-implemention';
import {
  FieldDataType,
  NumberFormat,
  SchemaField,
  SystemFieldType,
  TextFieldFormat,
  Workflow,
  WorkflowStep,
  WorkflowStepRule,
  WorkflowStepRuleAction
} from 'src/app/modules/core/store/models';
import { DateRuleOffsetType } from '../../workflow-rule-edit/workflow-rule-edit.model';
import { TranslateService } from '@ngx-translate/core';
import { SourceValueType, SetFieldValueRuleConfiguration } from './set-field-value-edit.model';
import moment from 'moment';
import { PicklistService } from 'src/app/modules/core/services/picklist/picklist.service';
import { take } from 'rxjs/operators';
import { CultureService } from 'src/app/modules/core/services/culture.service';
import { WorkflowRuleDataField } from 'src/app/modules/communications/store/workflow/workflow.models';
import { SystemNotificationsService } from '@app/core/system-notifications/system-notifications.service';

@Component({
    selector: 'topo-notification-rule-edit',
    templateUrl: './set-field-value-edit.component.html',
    styleUrls: ['./set-field-value-edit.component.scss'],
    standalone: false
})
export class SetFieldValueRuleEditComponent implements OnInit, OnDestroy, WorkflowRuleActionEdit {
  @Input() schema: WorkflowRuleDataField[];
  @Input() workflow: Workflow;
  @Input() step: WorkflowStep;
  @Input() rule: WorkflowStepRule;
  @Input() action: WorkflowStepRuleAction;
  @Input() implementationUId: string;
  @Input() readOnly: boolean;

  destroy$: Subject<void> = new Subject();

  isSourceValueSel: boolean;
  isSourceDateFieldSel: boolean;
  isSourceNumberFieldSel: boolean;
  isSourceDiffFieldSel: boolean;
  isOffsetSel: boolean;
  isOffsetPlusSel: boolean;
  isOffsetMinusSel: boolean;
  isOffsetValueSel: boolean;
  isDateSel: boolean;
  isNumberSel: boolean;
  isSourceSelectSel: boolean;
  isSourceColorSel: boolean;

  destFields: WorkflowRuleDataField[];
  sourceDateFields: WorkflowRuleDataField[];
  sourceDateDiffFields: WorkflowRuleDataField[];
  sourceNumberFields: WorkflowRuleDataField[];
  sourceSelectValues: any[];

  selectedDestField: string;
  selectedSourceField: string;
  selectedSourceNumberField: string;
  selectedSourceDiffField: string;
  selectedSourceType: SourceValueType;
  selectedOffsetType: DateRuleOffsetType;
  selectedOffsetValue: string;
  selectedSourceValue: string;

  sourceValueWidth: string = '200px';
  sourceValueHeight: string = '16px';

  dateSourceTypes = [
    { id: SourceValueType.SpecificValue, name: this.translationService.instant('MANAGEMENT.WORKFLOW.RULE-ACTION-VALUE-SPECIFIC') },
    { id: SourceValueType.DocumentField, name: this.translationService.instant('MANAGEMENT.WORKFLOW.RULE-ACTION-VALUE-FIELD') },
    { id: SourceValueType.Today, name: this.translationService.instant('MANAGEMENT.WORKFLOW.RULE-ACTION-VALUE-TODAY') }
  ];

  numberSourceTypes = [
    { id: SourceValueType.SpecificValue, name: this.translationService.instant('MANAGEMENT.WORKFLOW.RULE-ACTION-VALUE-SPECIFIC') },
    { id: SourceValueType.DocumentField, name: this.translationService.instant('MANAGEMENT.WORKFLOW.RULE-ACTION-VALUE-FIELD') },
    { id: SourceValueType.DateDiff, name: this.translationService.instant('MANAGEMENT.WORKFLOW.RULE-ACTION-VALUE-DATEDIFF') }
  ];

  offsetTypes = [
    { id: DateRuleOffsetType.Minutes, name: this.translationService.instant('FILTERS.FILTER-OPTIONS-6') },
    { id: DateRuleOffsetType.Hours, name: this.translationService.instant('FILTERS.FILTER-OPTIONS-1') },
    { id: DateRuleOffsetType.Days, name: this.translationService.instant('FILTERS.FILTER-OPTIONS-2') },
    { id: DateRuleOffsetType.Weeks, name: this.translationService.instant('FILTERS.FILTER-OPTIONS-3') },
    { id: DateRuleOffsetType.Months, name: this.translationService.instant('FILTERS.FILTER-OPTIONS-4') },
    { id: DateRuleOffsetType.Years, name: this.translationService.instant('FILTERS.FILTER-OPTIONS-5') }
  ];
  constructor(
    private translationService: TranslateService,
    private notify: SystemNotificationsService,
    private picklistService: PicklistService,
    private cultureService: CultureService
  ) {}

  load(): void {
    this.destFields = [];
    this.schema
      .filter(
        f =>
          f.dataType == FieldDataType.Text ||
          f.dataType == FieldDataType.DateTime ||
          f.dataType == FieldDataType.Number ||
          f.dataType == FieldDataType.Boolean ||
          f.dataType == FieldDataType.Picklist ||
          f.dataType == FieldDataType.Color ||
          f.dataType == FieldDataType.Email ||
          f.dataType == FieldDataType.Phone
      )
      .sort((a, b) => (a.displayName > b.displayName ? 1 : -1))
      .map(f => this.destFields.push(f));

    this.sourceDateFields = [];
    this.sourceDateDiffFields = [];
    this.sourceNumberFields = [];
    this.schema
      .filter(f => f.dataType === FieldDataType.DateTime)
      .sort((a, b) => (a.displayName > b.displayName ? 1 : -1))
      .map(f => {
        this.sourceDateFields.push(f);
        this.sourceDateDiffFields.push(f);
      });
    this.schema
      .filter(f => f.dataType === FieldDataType.Number)
      .sort((a, b) => (a.displayName > b.displayName ? 1 : -1))
      .map(f => this.sourceNumberFields.push(f));

    this.isSourceValueSel = true;
    this.isDateSel = false;
    this.isNumberSel = false;
    this.isOffsetPlusSel = this.isOffsetMinusSel = false;
    let settings = this.action?.configuration ? (JSON.parse(this.action.configuration) as SetFieldValueRuleConfiguration) : null;

    if (settings != null) {
      this.selectedSourceField = settings.sourceFieldName;
      this.selectedSourceValue = settings.sourceFieldValue;
      this.selectedDestField = settings.targetFieldName;
      this.selectedOffsetValue = settings.dateOffset?.toString();
      this.selectedOffsetType = settings.dateOffsetType;
      this.selectedSourceType = settings.sourceValueType;
      this.selectedSourceDiffField = settings.sourceDiffFieldName;
      this.selectedSourceNumberField = settings.sourceNumberFieldName;
    }

    this.selectDestField(false);
  }

  getAction(): WorkflowStepRuleAction {
    const action = this.action;
    return action;
  }

  destFieldSelected(): void {
    this.selectDestField(true);
  }

  private selectDestField(fromEvent: boolean): void {
    if (fromEvent) {
      this.selectedSourceValue = '';
      this.selectedSourceType = SourceValueType.SpecificValue;
      if (this.sourceDateFields.length) {
        this.selectedSourceField = this.sourceDateFields[0].systemName;
      }
      this.selectedOffsetValue = '1';
      this.selectedOffsetType = this.offsetTypes[0].id;
    }

    let field = this.destFields.find(f => f.systemName == this.selectedDestField);
    if (!!!field) {
      field = this.destFields[0];
      this.selectedDestField = field.systemName;
    }

    if (field.dataType === FieldDataType.Text) {
      this.sourceValueWidth = field?.format === TextFieldFormat.MultiLine ? '400px' : '200px';
      this.sourceValueHeight = field?.format === TextFieldFormat.MultiLine ? '200px' : '16px';
    }

    this.isDateSel = field.dataType === FieldDataType.DateTime;
    this.isNumberSel = field.dataType === FieldDataType.Number;

    this.isSourceSelectSel = false;
    this.isSourceValueSel = true;
    this.isSourceDateFieldSel = false;
    this.isSourceNumberFieldSel = false;
    this.isSourceColorSel = false;

    this.sourceSelectValues = [];

    if (field.dataType === FieldDataType.Boolean) {
      this.isSourceSelectSel = true;
      this.isSourceValueSel = false;
      this.sourceSelectValues = [
        { id: '1', name: this.translationService.instant('MANAGEMENT.WORKFLOW.RULE-ACTION-VALUE-TRUE') },
        { id: '0', name: this.translationService.instant('MANAGEMENT.WORKFLOW.RULE-ACTION-VALUE-FALSE') }
      ];
      if (!this.sourceSelectValues.find(s => s.id === this.selectedSourceValue)) {
        this.selectedSourceValue = '1';
      }
    } else if (field.dataType === FieldDataType.Picklist) {
      this.isSourceSelectSel = true;
      this.isSourceValueSel = false;

      this.picklistService
        .getPicklistValues(field)
        .pipe(take(1))
        .subscribe(picklistValues => {
          picklistValues?.map(v => this.sourceSelectValues.push({ id: v.uId, name: v.value }));
          if (!this.sourceSelectValues.find(s => s.id === this.selectedSourceValue)) {
            this.selectedSourceValue = this.sourceSelectValues.length ? this.sourceSelectValues[0].id : null;
          }
        });
    } else if (field.dataType === FieldDataType.Color) {
      this.isSourceSelectSel = false;
      this.isSourceValueSel = false;
      this.isSourceColorSel = true;
    }

    this.isOffsetSel = this.isDateSel;
    this.isOffsetValueSel = this.isDateSel;

    this.selectSelectedType(fromEvent);
  }

  sourceTypeSelected(): void {
    this.selectSelectedType(true);
  }

  sourceNumberTypeSelected(): void {
    this.selectSelectedType(true);
  }

  private selectSelectedType(fromEvent: boolean): void {
    if (this.isDateSel) {
      if (!this.selectedSourceType) {
        this.selectedSourceType = SourceValueType.SpecificValue;
      }
      if (!this.selectedSourceField && this.sourceDateFields.length) {
        this.selectedSourceField = this.sourceDateFields[0].systemName;
      }
      if (!this.selectedOffsetType) {
        this.selectedOffsetType = this.offsetTypes[0].id;
      }
      this.isSourceDateFieldSel = this.selectedSourceType === SourceValueType.DocumentField;
      this.isSourceNumberFieldSel = false;
      this.isSourceValueSel = this.selectedSourceType === SourceValueType.SpecificValue;
      this.isOffsetSel = this.selectedSourceType !== SourceValueType.SpecificValue;
      this.isOffsetValueSel = this.selectedSourceType !== SourceValueType.SpecificValue;
      this.isOffsetPlusSel = this.isOffsetValueSel;
    } else if (this.isNumberSel) {
      if (!this.selectedSourceType) {
        this.selectedSourceType = SourceValueType.SpecificValue;
      }
      if (!this.selectedSourceField && this.sourceDateFields.length) {
        this.selectedSourceField = this.sourceDateFields[0].systemName;
      }
      if (!this.selectedSourceDiffField && this.sourceDateDiffFields.length) {
        this.selectedSourceDiffField = this.sourceDateDiffFields[0].systemName;
      }
      if (!this.selectedSourceNumberField && this.sourceDateDiffFields.length) {
        this.selectedSourceNumberField = this.sourceNumberFields[0].systemName;
      }
      if (!this.selectedOffsetType) {
        this.selectedOffsetType = this.offsetTypes[0].id;
      }

      this.isSourceDateFieldSel = this.selectedSourceType === SourceValueType.DateDiff;
      this.isSourceValueSel = this.selectedSourceType === SourceValueType.SpecificValue;
      this.isSourceDiffFieldSel = this.selectedSourceType === SourceValueType.DateDiff;
      this.isOffsetSel = this.selectedSourceType === SourceValueType.DateDiff;
      this.isSourceNumberFieldSel = this.selectedSourceType === SourceValueType.DocumentField;
      this.isOffsetValueSel = this.selectedSourceType === SourceValueType.DocumentField;
      this.isOffsetMinusSel = this.isSourceDiffFieldSel;
      this.isOffsetPlusSel = this.isOffsetValueSel;
    } else {
      this.isSourceDateFieldSel = false;
      this.isSourceNumberFieldSel = false;
      this.isSourceValueSel = !this.isSourceSelectSel && !this.isSourceColorSel;
      this.isOffsetSel = false;
      this.isOffsetMinusSel = false;
      this.isOffsetPlusSel = false;
      this.isOffsetValueSel = false;
    }
  }

  private getDisplayText(): string {
    const destField = this.destFields.find(f => f.systemName === this.selectedDestField);
    const srcDateField = this.sourceDateFields.find(f => f.systemName === this.selectedSourceField);
    const srcDateDiffField = this.sourceDateFields.find(f => f.systemName === this.selectedSourceDiffField);
    const srcNumberField = this.sourceNumberFields.find(f => f.systemName === this.selectedSourceNumberField);
    const offsetType = this.offsetTypes.find(t => t.id === this.selectedOffsetType);
    const sourceSelectValue = this.sourceSelectValues.find(t => t.id === this.selectedSourceValue);

    let desc = this.translationService.instant('MANAGEMENT.WORKFLOW.RULE-ACTION-DESC-SET-DOC-VALUE');

    desc += ` <b>${destField.displayName}</b> = `;

    if (this.isNumberSel) {
      if (this.selectedSourceType === SourceValueType.DateDiff) {
        desc += ` (${srcDateField.displayName} - ${srcDateDiffField.displayName}) ${offsetType.name}`;
      } else {
        if (this.selectedSourceType === SourceValueType.DocumentField) {
          desc += srcNumberField.displayName;
          if (parseInt(this.selectedOffsetValue) > 0) {
            desc += ` + ${this.selectedOffsetValue}`;
          }
        } else {
          desc += this.selectedSourceValue;
        }
      }
    } else if (!this.isDateSel) {
      if (this.isSourceSelectSel) {
        desc += sourceSelectValue.name;
      } else {
        if (destField.dataType === FieldDataType.Number) {
          desc += this.selectedSourceValue;
        } else {
          desc += `"${this.selectedSourceValue}"`;
        }
      }
    } else {
      if (this.selectedSourceType === SourceValueType.Today) {
        desc += this.translationService.instant('MANAGEMENT.WORKFLOW.RULE-ACTION-VALUE-TODAY') + ' ';
      } else {
        if (this.selectedSourceType === SourceValueType.SpecificValue) {
          desc += this.selectedSourceValue;
        } else {
          desc += srcDateField.displayName;
        }
      }
      if (this.isOffsetSel && parseInt(this.selectedOffsetValue) > 0) {
        desc += ` + ${this.selectedOffsetValue} ${offsetType.name}`;
      }
    }

    return desc;
  }

  private getNumberValue(field: SchemaField, value: any): any {
    if (field.settings?.format === NumberFormat.Number) {
      return parseInt(value, 10);
    } else if (field.settings?.format === NumberFormat.Decimal) {
      return this.cultureService.localeParseFloat(value);
    } else if (field.settings?.format === NumberFormat.Accounting) {
      return this.cultureService.localeParseFloat(value);
    } else {
      return this.cultureService.localeParseFloat(value);
    }
  }

  private validate(): boolean {
    const destField = this.destFields.find(f => f.systemName === this.selectedDestField);

    if (destField.dataType == FieldDataType.Number && this.selectedSourceType === SourceValueType.SpecificValue) {
      const number = this.getNumberValue(destField, this.selectedSourceValue);
      if (isNaN(number)) {
        this.notify.error(
          this.translationService.instant('MANAGEMENT.WORKFLOW.RULE-ERROR-BAD-NUMBER', {
            value: this.selectedSourceValue === undefined ? '' : this.selectedSourceValue
          })
        );
        return false;
      }
    } else if (destField.dataType == FieldDataType.DateTime && this.selectedSourceType === SourceValueType.SpecificValue) {
      var date = moment(this.selectedSourceValue);
      if (!date.isValid()) {
        this.notify.error(
          this.translationService.instant('MANAGEMENT.WORKFLOW.RULE-ERROR-BAD-DATE', {
            value: this.selectedSourceValue === undefined ? '' : this.selectedSourceValue
          })
        );
        return false;
      }
    } else if (destField.dataType == FieldDataType.Color) {
      if (!!!this.selectedSourceValue) {
        this.notify.error(
          this.translationService.instant('MANAGEMENT.WORKFLOW.RULE-ERROR-BAD-COLOR', {
            value: this.selectedSourceValue === undefined ? '' : this.selectedSourceValue
          })
        );
        return false;
      }
    }
    return true;
  }

  save(): boolean {
    let dateOffset = parseInt(this.selectedOffsetValue);

    if (this.selectedSourceValue === null) {
      this.selectedSourceValue = '';
    }

    if (!this.validate()) {
      return false;
    }

    const destField = this.destFields.find(f => f.systemName === this.selectedDestField);
    if (destField.dataType == FieldDataType.Number && this.selectedSourceType === SourceValueType.SpecificValue) {
      this.selectedSourceValue = this.getNumberValue(destField, this.selectedSourceValue).toString();
    }

    const settings = {
      sourceFieldUId: this.selectedSourceField,
      sourceFieldValue: this.selectedSourceValue,
      targetFieldUId: this.selectedDestField,
      sourceValueType: this.selectedSourceType,
      dateOffset: isNaN(dateOffset) ? 0 : dateOffset,
      dateOffsetType: this.selectedOffsetType,
      sourceDiffFieldUId: this.selectedSourceDiffField,
      sourceNumberFieldUId: this.selectedSourceNumberField
    } as SetFieldValueRuleConfiguration;

    this.action.configuration = JSON.stringify(settings);
    this.action.displayText = this.getDisplayText();

    return true;
  }

  colorChanged(color: string) {
    this.selectedSourceValue = color;
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
